import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';
import { Formik, FormikHelpers } from 'formik';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Grid, Typography, Button } from '@material-ui/core';

import { newTabLink } from 'utils';
import { NewOrder } from 'types';
import { OrderTypeText, PageKey, UrlRoute } from 'enums';
import {
  BreadcrumbsCtx,
  initialBreadcrumbs,
} from 'components/portfolio/breadcrumbs-ctx';
import { AuthCtx } from 'components/auth/auth-ctx';
import { FormItemTextField } from 'components/form-item/form-item-text-field';
import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';
import { TextField } from 'components/text-field/text-field';
import { useInitialValues } from 'components/table/table-hook';

import { postNewOrder } from './order-api';
import { validationSchema } from './order-validation';
import { OrderAcknowledgeDialog } from './order-acknowledge-dialog';
import { FormItemSelectField } from 'components/form-item/form-item-select-field';
import { FormItemSingleFileField } from 'components/form-item/form-item-single-file-field';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    form: {
      color: 'black',
      marginBottom: 50,
    },
    link: {
      color: theme.palette.primary.main,
      marginRight: 15,
      textTransform: 'none',
    },
    unitLink: {
      width: '100%',
      color: theme.palette.primary.main,
      display: 'flex',
    },
    listItem: {
      padding: '5px 0px',
    },
    listItemName: {
      display: 'flex',
      justifyContent: 'flex-start',
      flexGrow: 1,
      textTransform: 'none',
    },
    iconButton: {
      width: 20,
      height: 20,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
      },
      '& svg': {
        fontSize: '1rem',
      },
    },
    submitButton: {
      margin: '15px 0',
      marginRight: '15px',
    },
  })
);

function NOOP() {}

export function ResearcherNewOrder() {
  const classes = useStyles();

  const history = useHistory();
  const [newOrderId, setNewOrderId] = useState<string | undefined>();
  const { setBreadcrumbs } = useContext(BreadcrumbsCtx);
  const { userInfo, userSheet, researchSheetToDisplayInOrder } = useContext(
    AuthCtx
  );
  const { showNotification } = useContext(NotificationCtx);
  const { locale, formatMessage } = useIntl();

  const { currentlyUsedSheetId } = userInfo ?? {};

  const today = format(new Date(), 'yyyy-MM-dd');

  const initialValues: NewOrder = useInitialValues(
    undefined,
    [],
    userInfo,
    userSheet
  );

  const handleSubmit = useCallback(
    async (values: NewOrder, actions: FormikHelpers<NewOrder>) => {
      try {
        const response = await postNewOrder(values);

        if (response) {
          showNotification(
            formatMessage({ id: 'aisOrder.successfullySent' }),
            NotificationVariant.SUCCESS
          );

          actions.resetForm();

          if (!!userSheet) {
            setNewOrderId(response.id);
          } else {
            history.push('/portfolio/historie-objednavek');
          }
        } else {
          throw new Error('Status not ok');
        }
      } catch (e) {
        showNotification(
          formatMessage({ id: 'aisOrder.failedToSend' }),
          NotificationVariant.ERROR
        );
      }

      actions.setSubmitting(false);
    },
    [formatMessage, history, showNotification, userSheet]
  );

  useEffect(() => {
    setBreadcrumbs([
      ...(userSheet?.id
        ? initialBreadcrumbs.userSheetBreadCrumbs
        : initialBreadcrumbs.breadcrumbs),
      {
        title: formatMessage({ id: 'aisOrder.researcherNewOrder' }),
        key: PageKey.PORTFOLIO_RESEARCHER_NEW_ORDER,
      },
    ]);
  }, [formatMessage, locale, setBreadcrumbs, userSheet]);

  const initial =
    userInfo?.currentlyUsedSheetType === 'CREDENTIAL'
      ? ({ ...initialValues, type: 'BASIC' } as NewOrder)
      : initialValues;
  return (
    <Grid container className={classes.root}>
      {!!currentlyUsedSheetId ? (
        <Grid item xs={10} md={6}>
          <div className={classes.form}>
            <Formik<NewOrder>
              initialValues={initial}
              enableReinitialize={true}
              onSubmit={handleSubmit}
              validateOnChange={false}
              validateOnBlur={false}
              validateOnMount={false}
              validationSchema={validationSchema}
            >
              {(props) => {
                const { handleSubmit, values } = props;
                return (
                  <form onSubmit={handleSubmit}>
                    <FormItemTextField
                      name="fullName"
                      label={formatMessage({ id: 'aisOrder.fullName' })}
                      disabled={true}
                    />
                    <TextField
                      name="currentlyUsedSheetOrdinalNumber"
                      label={formatMessage({
                        id: 'aisOrder.currentlyUsedSheetOrdinalNumber',
                      })}
                      value={researchSheetToDisplayInOrder ?? ''}
                      onChange={NOOP}
                      disabled={true}
                      formControlStyles={{ marginTop: 28 }}
                    />
                    <FormItemTextField
                      name="phone"
                      type="tel"
                      disabled={true}
                      label={formatMessage({
                        id: 'aisOrder.phone',
                        defaultMessage: 'Telefonní číslo',
                      })}
                    />
                    {userInfo?.currentlyUsedSheetType === 'CREDENTIAL' && (
                      <>
                        <FormItemSelectField
                          name="type"
                          label="Typ"
                          items={[
                            { id: 'BASIC', label: OrderTypeText.BASIC },
                            {
                              id: 'ORIGIN_ORIGINAL',
                              label: OrderTypeText.ORIGIN_ORIGINAL,
                            },
                            {
                              id: 'ORIGIN_LTP',
                              label: OrderTypeText.ORIGIN_LTP,
                            },
                          ]}
                        />
                        {values.type !== 'BASIC' && (
                          <FormItemSingleFileField
                            name="aisIdsCsv"
                            label="CSV soubor s identifikátory"
                          />
                        )}
                      </>
                    )}
                    <>
                      <Typography variant="body1" style={{ marginTop: 15 }}>
                        {formatMessage({ id: 'aisOrder.transferLinks' })}
                      </Typography>
                      <div>
                        <Button
                          className={classes.link}
                          onClick={() =>
                            newTabLink(`${UrlRoute.ARCHIVAL_COLLECTION}`)
                          }
                        >
                          <Typography component="span" variant="body2">
                            {formatMessage({
                              id: 'aisOrder.linkButtonCollections',
                            })}
                          </Typography>
                        </Button>
                        <Button
                          className={classes.link}
                          onClick={() => newTabLink(`${UrlRoute.ARCHIVAL_AID}`)}
                        >
                          <Typography component="span" variant="body2">
                            {formatMessage({ id: 'aisOrder.linkButtonAids' })}
                          </Typography>
                        </Button>
                      </div>
                    </>

                    <FormItemTextField
                      name="description"
                      label={formatMessage({
                        id: 'aisOrder.description',
                      })}
                      multiline={true}
                    />
                    <FormItemTextField
                      name="requestedForDate"
                      type="date"
                      label={formatMessage({ id: 'aisOrder.requestedForDate' })}
                    />
                    <Typography variant="body1" style={{ marginTop: 15 }}>
                      {formatMessage({ id: 'aisOrder.readyInfo' })}
                    </Typography>
                    <TextField
                      name="created"
                      type="date"
                      value={today}
                      label={formatMessage({ id: 'aisOrder.created' })}
                      onChange={NOOP}
                      disabled={true}
                      formControlStyles={{ marginTop: 28 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      className={classes.submitButton}
                      disabled={props.isSubmitting}
                    >
                      <Typography component="span" variant="body2">
                        {formatMessage({ id: 'aisOrder.submitButton' })}
                      </Typography>
                    </Button>
                  </form>
                );
              }}
            </Formik>
          </div>
        </Grid>
      ) : (
        <Grid item xs={10} md={6}>
          <Typography component="span" variant="body1" color="primary">
            {formatMessage({ id: 'aisOrder.withoutSheet' })}
          </Typography>
        </Grid>
      )}
      <OrderAcknowledgeDialog
        orderId={newOrderId}
        handleClose={() => setNewOrderId(undefined)}
      />
    </Grid>
  );
}
